import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useParams, useLocation } from 'react-router-dom';
import PixCheckoutComponent from '../components/payment/PixCheckoutComponent';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { SUBSCRIPTION_PLANS } from '../config/mercadopago';
import { ArrowLeft } from 'lucide-react';
import { supabase } from '../config/supabase';

const PixCheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const { purchaseId } = useParams();
  const [selectedPlan, setSelectedPlan] = useState<'premium' | 'enterprise'>('premium');
  const [pendingPurchase, setPendingPurchase] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [customPlan, setCustomPlan] = useState<any>(null);

  // Capturar o plano da URL ou carregar dados da compra pendente
  useEffect(() => {
    // Verificar se há dados de compra personalizados no state (vindo da página de Tokens)
    if (location.state && location.state.amount && location.state.price) {
      const { amount, price, description } = location.state;
      setCustomPlan({
        id: 'token_purchase',
        name: 'Pacote de Tokens',
        price: Number(price),
        description: description || `${amount} Tokens Overpixel`,
        features: [
          `${amount} Tokens para uso na plataforma`,
          'Aprovação imediata',
          'Sem validade',
          'Suporte incluído'
        ]
      });
      return;
    }

    const planFromUrl = searchParams.get('plan') as 'premium' | 'enterprise';
    if (planFromUrl && (planFromUrl === 'premium' || planFromUrl === 'enterprise')) {
      setSelectedPlan(planFromUrl);
    }

    // Se há um purchaseId, carregar os dados da transação pendente
    if (purchaseId) {
      loadPendingPurchase(purchaseId);
    }
  }, [searchParams, purchaseId]);

  const loadPendingPurchase = async (id: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('purchase_intents')
        .select('*')
        .eq('purchase_id', id)
        .eq('status', 'pending')
        .single();

      if (error) {
        console.error('Erro ao carregar compra pendente:', error);
        return;
      }

      if (data) {
        setPendingPurchase(data);
        // Definir o plano baseado no valor da transação
        const amount = data.amount || 0;
        if (amount >= 50) {
          setSelectedPlan('enterprise');
        } else {
          setSelectedPlan('premium');
        }
      }
    } catch (error) {
      console.error('Erro ao carregar compra pendente:', error);
    } finally {
      setLoading(false);
    }
  };

  // Redirecionar se não estiver autenticado
  if (!user) {
    navigate('/auth/login');
    return null;
  }

  const planDetails = customPlan || {
    ...SUBSCRIPTION_PLANS[selectedPlan],
    name: SUBSCRIPTION_PLANS[selectedPlan].title
  };

  const handleSuccess = () => {
    // Redirecionar para página de agradecimento com parâmetros de sucesso
    navigate('/thank-you?payment=success&source=pix');
  };

  const handleError = (error: string) => {
    console.error('Erro no checkout PIX:', error);
    // Manter na página para permitir nova tentativa
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors mb-4"
          >
            <ArrowLeft className="h-5 w-5" />
            Voltar
          </button>
          
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {pendingPurchase ? 'Finalizar Pagamento' : (customPlan ? 'Confirmar Compra' : 'Recarga de Tokens')}
            </h1>
            <p className="text-gray-600">
              {pendingPurchase 
                ? 'Complete o pagamento da sua transação pendente'
                : (customPlan 
                    ? 'Você está adquirindo tokens para turbinar sua criatividade' 
                    : 'Recarregue seus tokens de forma rápida e segura com PIX')
              }
            </p>
            {pendingPurchase && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 font-medium">
                  💰 Valor: R$ {(pendingPurchase.amount || pendingPurchase.metadata?.reais_amount || 0).toFixed(2)}
                </p>
                <p className="text-yellow-700 text-sm mt-1">
                  Tokens: {pendingPurchase.tokens_to_credit || pendingPurchase.metadata?.token_amount || 0} tokens
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Checkout Component */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando dados da transação...</p>
          </div>
        ) : (
          <PixCheckoutComponent 
            selectedPlan={selectedPlan}
            planDetails={planDetails}
            onSuccess={handleSuccess}
            onError={handleError}
            pendingPurchase={pendingPurchase}
          />
        )}

        {/* Informações de Segurança */}
        <div className="max-w-2xl mx-auto mt-8 p-6 bg-blue-50 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-3">🔒 Pagamento Seguro</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Processamento via Mercado Pago (certificado PCI DSS)</li>
            <li>• PIX é o método de pagamento mais seguro do Brasil</li>
            <li>• Aprovação instantânea, sem taxas adicionais</li>
            <li>• Seus dados são protegidos com criptografia de ponta</li>
          </ul>
        </div>

        {/* FAQ Rápido */}
        <div className="max-w-2xl mx-auto mt-6 p-6 bg-white rounded-lg shadow-sm">
          <h3 className="font-semibold text-gray-900 mb-4">❓ Dúvidas Frequentes</h3>
          <div className="space-y-3 text-sm">
            <div>
              <p className="font-medium text-gray-800">Quanto tempo demora para os tokens aparecerem?</p>
              <p className="text-gray-600">Os tokens são creditados automaticamente após a confirmação do PIX, geralmente em poucos segundos.</p>
            </div>
            <div>
              <p className="font-medium text-gray-800">Posso cancelar o pagamento?</p>
              <p className="text-gray-600">Sim, você pode fechar a janela de pagamento a qualquer momento antes de confirmar o PIX.</p>
            </div>
            <div>
              <p className="font-medium text-gray-800">E se o pagamento não for processado?</p>
              <p className="text-gray-600">Entre em contato conosco pelo suporte com o comprovante do PIX para resolvermos rapidamente.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PixCheckoutPage;